<template>
  <div class="min-h-screen bg-gray-50 p-6 dark:bg-gray-900">
    <div class="mb-6 flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
      <div>
        <h1 class="text-3xl font-bold text-gray-900 dark:text-gray-100">约课退款处理</h1>
        <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
          查看租户同意后的待退款申请。平台线下完成退款后，在这里标记订单已退款。
        </p>
      </div>
      <button
        type="button"
        class="rounded-xl border border-gray-300 bg-white px-4 py-3 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:opacity-60 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
        :disabled="loading"
        @click="loadRequests"
      >
        刷新数据
      </button>
    </div>

    <section class="mb-6 rounded-2xl border border-gray-100 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800">
      <div class="grid grid-cols-1 gap-4 md:grid-cols-3">
        <label>
          <span class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">状态</span>
          <select v-model="filters.status" class="form-input" @change="handleFilterChange">
            <option value="">全部状态</option>
            <option v-for="option in statusOptions" :key="option.value" :value="option.value">
              {{ option.label }}
            </option>
          </select>
        </label>
        <label>
          <span class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">租户</span>
          <select v-model.number="filters.tenantId" class="form-input" @change="handleFilterChange">
            <option :value="0">全部租户</option>
            <option v-for="tenant in tenants" :key="tenant.id" :value="tenant.id">
              {{ tenant.name }}
            </option>
          </select>
        </label>
      </div>
    </section>

    <section class="rounded-2xl border border-gray-100 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
      <div v-if="loading" class="flex min-h-[280px] items-center justify-center">
        <div class="text-center">
          <div class="mx-auto h-10 w-10 animate-spin rounded-full border-4 border-indigo-100 border-t-indigo-500" />
          <p class="mt-4 text-sm text-gray-500 dark:text-gray-400">正在加载退款申请...</p>
        </div>
      </div>

      <div v-else-if="requests.length === 0" class="px-6 py-14 text-center">
        <p class="text-base font-medium text-gray-700 dark:text-gray-200">暂无约课退款申请</p>
        <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">默认显示待平台退款，切换筛选可以查看历史记录。</p>
      </div>

      <div v-else class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead class="bg-gray-50 dark:bg-gray-900/60">
            <tr>
              <th class="table-th">租户</th>
              <th class="table-th">课程</th>
              <th class="table-th">学生 / 老师</th>
              <th class="table-th">订单</th>
              <th class="table-th">原因</th>
              <th class="table-th">状态</th>
              <th class="table-th">提交时间</th>
              <th class="table-th text-right">操作</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-200 dark:divide-gray-700">
            <tr v-for="item in requests" :key="item.id" class="hover:bg-gray-50 dark:hover:bg-gray-900/40">
              <td class="table-td font-medium text-gray-900 dark:text-gray-100">{{ item.tenantName || item.tenantId }}</td>
              <td class="table-td">
                <p class="font-medium text-gray-900 dark:text-gray-100">{{ item.courseTitle || `预约 #${item.bookingId}` }}</p>
                <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">{{ formatRange(item.startAt, item.endAt) }}</p>
              </td>
              <td class="table-td">
                <p>学生：{{ item.studentName || item.studentId }}</p>
                <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">老师：{{ item.tutorName || item.tutorId }}</p>
              </td>
              <td class="table-td min-w-[260px]">
                <p class="font-medium text-gray-900 dark:text-gray-100">
                  {{ item.orderAmount != null ? formatCurrency(item.orderAmount) : "--" }}
                </p>
                <div class="mt-2 space-y-1 text-xs text-gray-500 dark:text-gray-400">
                  <p class="flex items-center gap-2">
                    <span class="w-16 shrink-0">商户单号</span>
                    <code class="break-all rounded bg-gray-100 px-1.5 py-0.5 text-gray-700 dark:bg-gray-700 dark:text-gray-200">
                      {{ item.orderOutTradeNo || "--" }}
                    </code>
                    <button v-if="item.orderOutTradeNo" type="button" class="copy-btn" @click="copyText(item.orderOutTradeNo)">复制</button>
                  </p>
                  <p class="flex items-center gap-2">
                    <span class="w-16 shrink-0">平台单号</span>
                    <code class="break-all rounded bg-gray-100 px-1.5 py-0.5 text-gray-700 dark:bg-gray-700 dark:text-gray-200">
                      {{ item.orderTradeNo || "--" }}
                    </code>
                    <button v-if="item.orderTradeNo" type="button" class="copy-btn" @click="copyText(item.orderTradeNo)">复制</button>
                  </p>
                  <p>支付方式：{{ item.orderPaymentMethod || "--" }}</p>
                  <p>支付时间：{{ formatDateTime(item.orderPaidAt) }}</p>
                </div>
              </td>
              <td class="table-td max-w-xs">
                <p class="font-medium">{{ reasonLabel(item.reason) }}</p>
                <p class="mt-1 line-clamp-2 text-xs text-gray-500 dark:text-gray-400">{{ item.description || "未填写说明" }}</p>
              </td>
              <td class="table-td">
                <span class="rounded-full px-2.5 py-1 text-xs font-semibold" :class="statusClass(item.status)">
                  {{ statusLabel(item.status) }}
                </span>
              </td>
              <td class="table-td">{{ formatDateTime(item.createdAt) }}</td>
              <td class="table-td text-right">
                <div v-if="item.status === 'pending_platform_review'" class="flex justify-end gap-2">
                  <button class="action-refund" type="button" @click="openMarkRefunded(item)">标记已退款</button>
                  <button class="action-reject" type="button" @click="openReject(item)">拒绝</button>
                </div>
                <span v-else class="text-xs text-gray-400">无可操作</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="pagination.total > 0" class="border-t border-gray-200 bg-gray-50 px-6 py-4 dark:border-gray-700 dark:bg-gray-900/60">
        <div class="flex items-center justify-between">
          <div class="text-sm text-gray-700 dark:text-gray-300">共 {{ pagination.total }} 条</div>
          <div class="flex gap-2">
            <button class="page-btn" type="button" :disabled="pagination.page === 1" @click="prevPage">上一页</button>
            <button class="page-btn" type="button" :disabled="pagination.page * pagination.limit >= pagination.total" @click="nextPage">下一页</button>
          </div>
        </div>
      </div>
    </section>

    <div v-if="modalTarget" class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div class="w-full max-w-xl rounded-2xl bg-white p-6 shadow-xl dark:bg-gray-900">
        <h2 class="text-lg font-semibold text-gray-900 dark:text-gray-100">
          {{ modalAction === "mark_refunded" ? "标记已人工退款" : "拒绝退款申请" }}
        </h2>
        <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">
          {{ modalAction === "mark_refunded" ? "请先在支付后台或线下完成退款，再标记系统状态。" : "拒绝后订单仍保持已支付状态。" }}
        </p>

        <div class="mt-5 rounded-xl border border-gray-200 bg-gray-50 p-4 text-sm dark:border-gray-700 dark:bg-gray-800">
          <div class="grid grid-cols-1 gap-3 md:grid-cols-2">
            <p>
              <span class="text-gray-500 dark:text-gray-400">订单金额：</span>
              <span class="font-medium text-gray-900 dark:text-gray-100">
                {{ modalTarget.orderAmount != null ? formatCurrency(modalTarget.orderAmount) : "--" }}
              </span>
            </p>
            <p>
              <span class="text-gray-500 dark:text-gray-400">支付方式：</span>
              <span class="font-medium text-gray-900 dark:text-gray-100">{{ modalTarget.orderPaymentMethod || "--" }}</span>
            </p>
            <p class="md:col-span-2">
              <span class="text-gray-500 dark:text-gray-400">商户订单号：</span>
              <code class="break-all rounded bg-white px-1.5 py-0.5 dark:bg-gray-900">{{ modalTarget.orderOutTradeNo || "--" }}</code>
            </p>
            <p class="md:col-span-2">
              <span class="text-gray-500 dark:text-gray-400">支付平台交易号：</span>
              <code class="break-all rounded bg-white px-1.5 py-0.5 dark:bg-gray-900">{{ modalTarget.orderTradeNo || "--" }}</code>
            </p>
          </div>
        </div>

        <div v-if="modalAction === 'mark_refunded'" class="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2">
          <label>
            <span class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">退款金额（元）</span>
            <input v-model.trim="refundForm.refundAmountYuan" type="number" min="0" step="0.01" class="form-input" />
          </label>
          <label>
            <span class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">退款方式</span>
            <input v-model.trim="refundForm.refundMethod" type="text" class="form-input" placeholder="例如：epay后台 / 微信 / 人工" />
          </label>
          <label class="md:col-span-2">
            <span class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">退款凭证/流水号</span>
            <input v-model.trim="refundForm.refundReference" type="text" class="form-input" placeholder="可选，填写支付平台退款单号或备注编号" />
          </label>
        </div>

        <label class="mt-5 block">
          <span class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">平台备注</span>
          <textarea
            v-model="refundForm.platformReviewNote"
            rows="5"
            maxlength="2000"
            class="form-input"
            placeholder="可选，记录平台处理说明"
          />
        </label>

        <div class="mt-6 flex justify-end gap-3">
          <button class="page-btn" type="button" :disabled="submitting" @click="closeModal">取消</button>
          <button
            type="button"
            class="rounded-xl px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
            :class="modalAction === 'mark_refunded' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-rose-600 hover:bg-rose-700'"
            :disabled="submitting"
            @click="submitModal"
          >
            {{ submitting ? "提交中..." : "确认" }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, reactive, ref } from "vue";

import { getTenantsAPI } from "@/api/admin/tenants";
import {
  getAdminTutorRefundRequestsAPI,
  markAdminTutorRefundRequestRefundedAPI,
  rejectAdminTutorRefundRequestAPI,
} from "@/api/admin/tutorRefund";
import type { AdminTenantConfig } from "@/types/admin/tenant";
import type {
  TutorLessonRefundRequest,
  TutorLessonRefundRequestReason,
  TutorLessonRefundRequestStatus,
} from "@/types/admin/tutorRefund";
import { formatCurrency, formatDateTime } from "@/utils/formatters";
import toaster from "@/utils/toaster";

type ModalAction = "mark_refunded" | "reject";

const tenants = ref<AdminTenantConfig[]>([]);
const requests = ref<TutorLessonRefundRequest[]>([]);
const loading = ref(false);
const submitting = ref(false);
const modalTarget = ref<TutorLessonRefundRequest | null>(null);
const modalAction = ref<ModalAction>("mark_refunded");

const filters = reactive<{ status: TutorLessonRefundRequestStatus | ""; tenantId: number }>({
  status: "pending_platform_review",
  tenantId: 0,
});
const pagination = reactive({
  page: 1,
  limit: 20,
  total: 0,
});
const refundForm = reactive({
  refundAmountYuan: "",
  refundMethod: "",
  refundReference: "",
  platformReviewNote: "",
});

const statusOptions: Array<{ label: string; value: TutorLessonRefundRequestStatus }> = [
  { label: "待机构审核", value: "pending_tenant_review" },
  { label: "待平台退款", value: "pending_platform_review" },
  { label: "机构已拒绝", value: "tenant_rejected" },
  { label: "平台已拒绝", value: "platform_rejected" },
  { label: "平台已退款", value: "platform_refunded" },
  { label: "已取消", value: "cancelled" },
];

const statusLabel = (status: TutorLessonRefundRequestStatus): string => {
  const map: Record<TutorLessonRefundRequestStatus, string> = {
    pending_tenant_review: "待机构审核",
    pending_platform_review: "待平台退款",
    tenant_rejected: "机构已拒绝",
    platform_rejected: "平台已拒绝",
    platform_refunded: "平台已退款",
    cancelled: "已取消",
  };
  return map[status];
};

const statusClass = (status: TutorLessonRefundRequestStatus): string => {
  if (status === "pending_platform_review") return "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300";
  if (status === "pending_tenant_review") return "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300";
  if (status === "platform_refunded") return "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300";
  return "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300";
};

const reasonLabel = (reason: TutorLessonRefundRequestReason): string => {
  const map: Record<TutorLessonRefundRequestReason, string> = {
    teacher_no_show: "老师未上课",
    classroom_problem: "课堂问题",
    teacher_late_or_left_early: "老师迟到/早退",
    quality_problem: "课程质量问题",
    other: "其他",
  };
  return map[reason];
};

const formatRange = (startAt: string | null, endAt: string | null): string => {
  if (!startAt || !endAt) return "时间未记录";
  return `${formatDateTime(startAt)} - ${formatDateTime(endAt)}`;
};

const copyText = async (value: string) => {
  try {
    await navigator.clipboard.writeText(value);
    toaster.success("已复制订单号");
  } catch (error) {
    toaster.error("复制失败，请手动选择订单号");
    console.error(error);
  }
};

const loadTenants = async () => {
  const res = await getTenantsAPI({ page: 1, limit: 200 });
  if (res.status === 200 && res.data) {
    tenants.value = res.data.tenants;
  }
};

const loadRequests = async () => {
  loading.value = true;
  try {
    const res = await getAdminTutorRefundRequestsAPI({
      page: pagination.page,
      limit: pagination.limit,
      status: filters.status,
      tenantId: filters.tenantId || null,
    });
    if (res.status === 200 && res.data) {
      requests.value = res.data.requests;
      pagination.total = res.data.total;
      pagination.page = res.data.page;
      pagination.limit = res.data.limit;
    }
  } catch (error) {
    toaster.error("加载约课退款申请失败");
    console.error(error);
  } finally {
    loading.value = false;
  }
};

const handleFilterChange = () => {
  pagination.page = 1;
  loadRequests();
};

const prevPage = () => {
  if (pagination.page <= 1) return;
  pagination.page -= 1;
  loadRequests();
};

const nextPage = () => {
  if (pagination.page * pagination.limit >= pagination.total) return;
  pagination.page += 1;
  loadRequests();
};

const resetRefundForm = () => {
  refundForm.refundAmountYuan = "";
  refundForm.refundMethod = "";
  refundForm.refundReference = "";
  refundForm.platformReviewNote = "";
};

const openMarkRefunded = (request: TutorLessonRefundRequest) => {
  modalTarget.value = request;
  modalAction.value = "mark_refunded";
  resetRefundForm();
  refundForm.refundAmountYuan = request.orderAmount != null ? (request.orderAmount / 100).toFixed(2) : "";
};

const openReject = (request: TutorLessonRefundRequest) => {
  modalTarget.value = request;
  modalAction.value = "reject";
  resetRefundForm();
};

const closeModal = () => {
  if (submitting.value) return;
  modalTarget.value = null;
  resetRefundForm();
};

const replaceRequest = (nextRequest: TutorLessonRefundRequest) => {
  requests.value = requests.value.map((item) => (item.id === nextRequest.id ? nextRequest : item));
};

const submitMarkRefunded = async (target: TutorLessonRefundRequest) => {
  const yuan = Number.parseFloat(refundForm.refundAmountYuan);
  if (!Number.isFinite(yuan) || yuan < 0) {
    toaster.error("请输入有效退款金额");
    return;
  }

  const res = await markAdminTutorRefundRequestRefundedAPI(target.id, {
    refundAmount: Math.round(yuan * 100),
    refundMethod: refundForm.refundMethod.trim() || null,
    refundReference: refundForm.refundReference.trim() || null,
    platformReviewNote: refundForm.platformReviewNote.trim() || null,
  });
  if (res.status === 200 && res.data) {
    replaceRequest(res.data);
    toaster.success("已标记为人工退款");
    closeModal();
  } else {
    toaster.error(res.message || "处理失败");
  }
};

const submitReject = async (target: TutorLessonRefundRequest) => {
  const res = await rejectAdminTutorRefundRequestAPI(target.id, {
    platformReviewNote: refundForm.platformReviewNote.trim() || null,
  });
  if (res.status === 200 && res.data) {
    replaceRequest(res.data);
    toaster.success("已拒绝退款申请");
    closeModal();
  } else {
    toaster.error(res.message || "处理失败");
  }
};

const submitModal = async () => {
  if (!modalTarget.value) return;
  submitting.value = true;
  try {
    if (modalAction.value === "mark_refunded") {
      await submitMarkRefunded(modalTarget.value);
    } else {
      await submitReject(modalTarget.value);
    }
  } catch (error) {
    toaster.error("处理失败");
    console.error(error);
  } finally {
    submitting.value = false;
  }
};

onMounted(async () => {
  await loadTenants();
  await loadRequests();
});
</script>

<style scoped>
.form-input {
  @apply w-full rounded-xl border border-gray-300 bg-white px-4 py-3 text-sm text-gray-900 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 dark:focus:ring-indigo-900;
}

.table-th {
  @apply px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400;
}

.table-td {
  @apply px-4 py-4 text-sm text-gray-600 dark:text-gray-300;
}

.page-btn {
  @apply rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700;
}

.action-refund {
  @apply rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700;
}

.action-reject {
  @apply rounded-lg bg-rose-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-rose-700;
}

.copy-btn {
  @apply shrink-0 rounded-md border border-gray-300 px-2 py-0.5 text-xs text-gray-600 hover:bg-gray-100 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700;
}
</style>
